import {Ionicons} from '@expo/vector-icons';
import {PlatformPressable} from '@react-navigation/elements';
import {useTheme as useNavigationTheme} from '@react-navigation/native';
import type {
  StackNavigationOptions,
  StackScreenProps,
} from '@react-navigation/stack';
import {createStackNavigator, TransitionPresets} from '@react-navigation/stack';
import {useCallback, useMemo} from 'react';
import {Platform, StyleSheet} from 'react-native';

import ListCitiesBinding from '../bindings/ListCitiesBinding/ListCitiesBinding';
import ListGroupsBinding from '../bindings/ListGroupsBinding/ListGroupsBinding';
import {PromptSettingsBinding} from '../bindings/PromptSettingsBinding';
import PromptSetupBinding from '../bindings/PromptSetupBinding';
import {ShowDailyBinding} from '../bindings/ShowDailyBinding';
import ShowGroupBinding from '../bindings/ShowGroupBinding/ShowGroupBinding';
import {ShowInsightBinding} from '../bindings/ShowInsightBinding';
import {ShowMeetingCardBinding} from '../bindings/ShowMeetingCardBinding';
import {ShowProgressBinding} from '../bindings/ShowProgressBinding';
import type {RootStackParamList} from './RootStackParamList';

export default function RootStack() {
  const theme = useNavigationTheme();
  const options = useMemo<StackNavigationOptions>(() => {
    const tintColor = Platform.select({
      ios: theme.colors.primary,
      default: theme.colors.text,
    });
    const pressColor = theme.dark
      ? 'rgba(255, 255, 255, .32)'
      : 'rgba(0, 0, 0, .32)';
    return {
      ...commonOptions,
      headerTintColor: tintColor,
      headerPressColor: pressColor,
      headerPressOpacity: 0.3,
    };
  }, [theme]);
  const showMeetingCardOptions = useShowMeetingCardOptions();

  return (
    <Navigator
      id="root"
      initialRouteName="ShowProgress"
      screenOptions={options}>
      <Screen
        name="ShowProgress"
        component={ShowProgressBinding}
        options={showProgressOptions}
      />
      <Screen
        name="PromptSetup"
        component={PromptSetupBinding}
        options={promptSetupOptions}
      />
      <Screen
        name="PromptSettings"
        component={PromptSettingsBinding}
        options={promptSettingsOptions}
      />
      <Screen name="ShowDaily" component={ShowDailyBinding} />
      <Screen name="ShowInsight" component={ShowInsightBinding} />
      <Screen
        name="ShowMeetingCard"
        component={ShowMeetingCardBinding}
        options={showMeetingCardOptions}
      />
      <Screen name="ListCities" component={ListCitiesBinding} />
      <Screen name="ListGroups" component={ListGroupsBinding} />
      <Screen name="ShowGroup" component={ShowGroupBinding} />
    </Navigator>
  );
}

const commonOptions: StackNavigationOptions = {
  headerBackTitleVisible: false,
  headerTransparent: true,
  headerMode: 'float',
  headerTitle: '',
  ...TransitionPresets.DefaultTransition,
};

function showProgressOptions(
  props: StackScreenProps<RootStackParamList, 'ShowProgress'>,
): StackNavigationOptions {
  return {
    ...withSettingsButton(props),
    title: 'Прогресс',
  };
}

function useShowMeetingCardOptions() {
  return useCallback(
    (
      props: StackScreenProps<RootStackParamList, 'ShowMeetingCard'>,
    ): StackNavigationOptions => ({
      ...withSettingsButton(props),
      title: 'Карточка собрания',
      headerTintColor: '#fff',
      headerPressColor: 'rgba(255, 255, 255, .32)',
      headerBackgroundContainerStyle: {
        backgroundColor: '#00000033',
      },
    }),
    [],
  );
}

function withSettingsButton(
  props: StackScreenProps<RootStackParamList>,
): StackNavigationOptions {
  const {navigation} = props;
  return {
    headerShown: true,
    headerRight: _props => {
      const {pressColor, pressOpacity, tintColor} = _props;
      return (
        <PlatformPressable
          onPress={() => {
            navigation.navigate('PromptSettings');
          }}
          pressColor={pressColor}
          pressOpacity={pressOpacity}>
          <Ionicons
            name="settings-outline"
            size={24}
            color={tintColor}
            style={styles.settingsIcon}
          />
        </PlatformPressable>
      );
    },
  };
}

const styles = StyleSheet.create({
  settingsIcon: {
    marginHorizontal: 14,
  },
});

const promptSetupOptions: StackNavigationOptions = {
  title: 'Установка',
  presentation: 'modal',
};

const promptSettingsOptions: StackNavigationOptions = {
  title: 'Настройки',
  presentation: 'modal',
};

const {Screen, Navigator} = createStackNavigator<RootStackParamList>();
